import { getFusionHostOrigin, isFusionFramed } from './FusionThemeBridge'

/**
 * Requests the current facade JWT from the Fusion (new-UI) host over the parent⇄iframe postMessage
 * channel. Used by the embedded auth-page recovery ({@code FusionAuthRecovery}) to re-establish a
 * dropped CCS session without showing the login form (CXUI-583).
 *
 * The CCS iframe posts {@link FUSION_JWT_REQUEST_MESSAGE_TYPE} up to the host; the host replies with
 * {@link FUSION_JWT_RESPONSE_MESSAGE_TYPE} carrying the token, or {@link FUSION_JWT_EXPIRED_MESSAGE_TYPE}
 * when its own JWT is genuinely expired (so recovery stops and the host re-authenticates instead of
 * looping).
 *
 * Keep these type strings in sync with the host contract in akixi-frontend:
 *   packages/ui/src/features/legacy-mode/model/ccsMessages.ts
 */
export const FUSION_JWT_REQUEST_MESSAGE_TYPE = 'akixi:fusion:jwt:request'
export const FUSION_JWT_RESPONSE_MESSAGE_TYPE = 'akixi:fusion:jwt:response'
export const FUSION_JWT_EXPIRED_MESSAGE_TYPE = 'akixi:fusion:jwt:expired'

const DEFAULT_JWT_REQUEST_TIMEOUT_MS = 8000

/**
 * Asks the Fusion host for the current facade JWT and resolves with it, or {@code null} when this
 * page is standalone, the host origin is not yet known, the host reports its JWT expired, or no
 * response arrives within {@code timeoutMs}. Best effort — never rejects.
 *
 * @param timeoutMs - How long to wait for the host's reply before giving up.
 * @return The current facade JWT, or {@code null}.
 */
export const requestJwtFromParent = (
    timeoutMs: number = DEFAULT_JWT_REQUEST_TIMEOUT_MS
): Promise<OnlyNullable<string>> => {
    const hostOrigin: Nullable<string> = getFusionHostOrigin()

    if (!isFusionFramed() || !hostOrigin) {
        return Promise.resolve(null)
    }

    return new Promise<OnlyNullable<string>>((resolve): void => {
        let settled = false

        const finish = (token: OnlyNullable<string>): void => {
            if (settled) return

            settled = true
            window.clearTimeout(timer)
            window.removeEventListener('message', onMessage)
            resolve(token)
        }

        const onMessage = (event: MessageEvent): void => {
            // Only trust the actual embedding parent and the origin we learnt from it (same guards as
            // the theme/reauth bridges) so a third party cannot feed us a forged token.
            if (event.source !== window.parent) return

            if (event.origin !== getFusionHostOrigin()) return

            const data = event.data as Nullable<{ type?: unknown; token?: unknown }>

            if (data?.type === FUSION_JWT_EXPIRED_MESSAGE_TYPE) {
                finish(null)

                return
            }

            if (data?.type !== FUSION_JWT_RESPONSE_MESSAGE_TYPE) return

            finish(typeof data.token === 'string' && data.token.length > 0 ? data.token : null)
        }

        const timer: number = window.setTimeout((): void => finish(null), timeoutMs)

        window.addEventListener('message', onMessage)

        try {
            window.parent.postMessage({ type: FUSION_JWT_REQUEST_MESSAGE_TYPE }, hostOrigin)
        } catch {
            finish(null)
        }
    })
}
